import {
  Clickable,
  DrawerProps,
  Icon,
  isNumber,
  useResizeObserver,
  withDisplayName
} from "@madhive/kit-ui";
import { useMadState, useMadSDKFeatureFlag } from "@madhive/mad-hooks";
import type {
  AgentProductContext,
  RemoteConfigProperties
} from "@madhive/mad-sdk";
import { RefObject, useRef, useState } from "react";
import type { AgentChatDrawerProps } from "./types";
import { styles } from "./styles";

type AgentChatDrawerComponent = React.ComponentType<AgentChatDrawerProps>;

/**
 * Creates a `makeInfoDrawer` factory bound to a specific Drawer component.
 * Called once in AgentChat.tsx to produce `AgentChat.makeInfoDrawer`.
 * @internal
 */
export function createMakeInfoDrawer(
  AgentChatDrawer: AgentChatDrawerComponent
) {
  /**
   * Creates an `InfoAgentChat` component bound to the specified `AgentChatDrawer`.
   * This component manages the drawer state and merges default and custom props.
   */
  return function makeInfoDrawer({
    name,
    product,
    useFeatureFlag = useMadSDKFeatureFlag,
    sessionId: defaultSessionId = `${product}:info`,
    ...defaults
  }: MakeInfoChatConfig) {
    /**
     * Info agent chat component that renders the chat drawer.
     */
    function InfoAgentChat({
      children,
      featureFlag = defaults.featureFlag,
      textInputFlag = defaults.textInputFlag,
      topOffset: topOffsetProp,
      sessionId = defaultSessionId,
      drawerProps: {
        onClose = defaults.drawerProps?.onClose,
        onOpen = defaults.drawerProps?.onOpen,
        ...drawerProps
      } = {},
      ...props
    }: InfoAgentChatProps) {
      const [isOpen, setIsOpen] = useDrawerState(sessionId);
      const isTextInputEnabled = useFeatureFlag(textInputFlag || []);

      const [topOffsetState, setTopOffset] = useState(
        isNumber(topOffsetProp) ? topOffsetProp : 0
      );
      const topOffset =
        typeof topOffsetProp === "number" ? topOffsetProp : topOffsetState;
      const nullHeaderRef = useRef(null);
      const headerRef =
        typeof topOffsetProp === "object" && "current" in topOffsetProp
          ? topOffsetProp
          : nullHeaderRef;

      useResizeObserver(headerRef, () => {
        setTopOffset(headerRef.current?.getBoundingClientRect().height || 0);
      });

      return (
        <Guard fallback={children} featureFlag={featureFlag}>
          <AgentChatDrawer
            {...defaults}
            {...props}
            agent="info"
            context={{ product }}
            inputProps={{
              ...defaults.inputProps,
              ...props.inputProps,
              ...(textInputFlag ? { noTextarea: !isTextInputEnabled } : {})
            }}
            cues={{
              ...defaults.cues,
              ...props.cues
            }}
            sessionId={sessionId}
            feedbackProps={{
              ...defaults.feedbackProps,
              ...props.feedbackProps
            }}
            drawerProps={{
              ...defaults.drawerProps,
              ...drawerProps,
              isOpen,
              topOffset,
              onClose: () => {
                setIsOpen(false);
                onClose?.();
              },
              onOpen: () => {
                setIsOpen(true);
                onOpen?.();
              }
            }}
          >
            {children}
          </AgentChatDrawer>
        </Guard>
      );
    }

    /**
     * Manages the open state of the info agent chat drawer for a given session ID.
     * Uses the `useMadState` hook to persist the open state across renders — based on the session ID.
     */
    function useDrawerState(sessionId: string = defaultSessionId) {
      const [isOpen, setIsOpen] = useMadState(
        `${product}:chat:drawer:${sessionId}`,
        false
      );
      return [isOpen, setIsOpen] as const;
    }

    /**
     * Toggle button for the info agent chat drawer.
     * Renders a button that toggles the open state of the info agent chat drawer.
     * The button respects the feature flag and disabled state.
     */
    function Toggle({
      sessionId = defaultSessionId,
      featureFlag = defaults.featureFlag,
      onClick,
      disabled,
      children = <Icon name="Spark" styled size="medium" />
    }: InfoAgentChatToggleProps) {
      const [isOpen, setIsOpen] = useDrawerState(sessionId);

      return (
        <Guard featureFlag={featureFlag}>
          <Clickable
            aria-label="Toggle Info Agent Chat"
            aria-expanded={isOpen}
            disabled={disabled}
            onClick={() => {
              setIsOpen(!isOpen);
              onClick?.();
            }}
            css={[
              !isOpen && styles.toggle.isClosed,
              isOpen && styles.toggle.isOpen
            ]}
          >
            {children}
          </Clickable>
        </Guard>
      );
    }

    /**
     * Guards the rendering of its children based on the specified feature flag.
     * If the feature flag is not enabled, renders the fallback content instead.
     */
    function Guard({
      children,
      featureFlag = defaults.featureFlag,
      fallback = null
    }: {
      children: React.ReactNode;
      fallback?: React.ReactNode;
      featureFlag?: InfoAgentChatProps["featureFlag"];
    }) {
      const isEnabled = useFeatureFlag(featureFlag || []);
      if (featureFlag && !isEnabled) {
        return fallback;
      }
      return children;
    }

    InfoAgentChat.displayName = name;
    InfoAgentChat.Toggle = withDisplayName(Toggle, `${name}.Toggle`);
    InfoAgentChat.useDrawerState = useDrawerState;
    InfoAgentChat.SESSION_ID = defaultSessionId;

    return InfoAgentChat;
  };
}

/**
 * Props that the factory locks — consumers cannot override these.
 */
type LockedProps = "agent" | "context";

/**
 * AgentChat.Drawer props that can be defaulted at factory time
 * or overridden at render time.
 */
type OverridableDrawerProps = Partial<
  Omit<AgentChatDrawerProps, LockedProps | "children" | "drawerProps">
> & {
  drawerProps?: Omit<
    Required<AgentChatDrawerProps>["drawerProps"],
    "isOpen" | "topOffset"
  >;
};

/**
 * Configuration for `AgentChat.makeInfoDrawer`.
 * Only `name` and `product` are required — everything else has sensible defaults.
 */
export type MakeInfoChatConfig = {
  /** Display name used in React DevTools (e.g. "CampaignsInfoAgentChat"). */
  name: string;
  /** Product context sent to the info agent backend. */
  product: AgentProductContext;
  /** Hook for evaluating feature flags. If omitted, {@link useMadSDKFeatureFlag} from mad-hooks will be used. */
  useFeatureFlag?: typeof useMadSDKFeatureFlag;
  /** Session ID. Defaults to `${product}:info`. */
  sessionId?: string;
  /** Feature flag that gates rendering. If omitted, the chat is always rendered. */
  featureFlag?: RemoteConfigProperties | RemoteConfigProperties[];
  /** Feature flag that controls textarea visibility. If omitted, textarea is always visible. */
  textInputFlag?: RemoteConfigProperties | RemoteConfigProperties[];
} & OverridableDrawerProps;

/**
 * Props accepted by the component returned from `AgentChat.makeInfoDrawer`.
 * All AgentChatDrawerProps (minus locked props) can be passed and will be
 * shallow-merged over factory defaults.
 */
type InfoAgentChatProps = OverridableDrawerProps & {
  /** Content to be rendered inside the info agent chat component. */
  children?: React.ReactNode;
  /** The top offset for the drawer. Can be a number or a ref to an HTML element (typically the page header's ref). */
  topOffset?: DrawerProps["topOffset"] | RefObject<HTMLElement>;
} & Omit<MakeInfoChatConfig, "name" | "product" | "useFeatureFlag">;

type InfoAgentChatToggleProps = {
  /** Optional click handler when toggle is clicked */
  onClick?: (() => void) | undefined;
  /** The content to be rendered inside the toggle button. Defaults to a Spark icon. */
  children?: React.ReactNode;
  /** Whether the toggle button is disabled. */
  disabled?: boolean;
} & Pick<InfoAgentChatProps, "sessionId" | "featureFlag">;
